import { useQuery } from '@tanstack/react-query';
import { supabase, type UserRole } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type { UserRole };

export interface UserPermissions {
    role: UserRole;
    isMaster: boolean;
    isAdmin: boolean;
    isGerente: boolean;
    isInstrutor: boolean;
    isMonitor: boolean;
    isModerator: boolean;
    isUser: boolean;
    canModerate: boolean;
    canBanUsers: boolean;
    canDeleteAnyContent: boolean;
    canPinPosts: boolean;
    canManageCourses: boolean;
    canManageLives: boolean;
    canViewReports: boolean;
    canManageUsers: boolean;
    canManageRoles: boolean;
}

const roleOrder: Record<string, number> = {
    master: 7,
    admin: 6,
    gerente: 5,
    moderator: 4,
    instrutor: 3,
    monitor: 2,
    user: 1,
};

export function useUserRole() {
    const { user } = useAuth();

    const { data: userRoleData, isLoading } = useQuery({
        queryKey: ['userRole', user?.id],
        queryFn: async () => {
            if (!user) return null;

            const [rolesResult, profileResult] = await Promise.all([
                supabase
                    .from('user_roles')
                    .select('role')
                    .eq('user_id', user.id),
                supabase
                    .from('user_profiles')
                    .select('is_banned, plan_type')
                    .eq('id', user.id)
                    .maybeSingle()
            ]);

            if (rolesResult.error) throw rolesResult.error;
            if (profileResult.error) throw profileResult.error;

            // Pick the highest-privilege role
            const roles = rolesResult.data || [];
            let highestRole: UserRole = 'user';
            let highestOrder = 0;
            for (const r of roles) {
                const roleName = r.role as UserRole;
                const order = roleOrder[roleName] || 0;
                if (order > highestOrder) {
                    highestOrder = order;
                    highestRole = roleName;
                }
            }

            return {
                role: highestRole,
                is_banned: profileResult.data?.is_banned || false,
                plan_type: profileResult.data?.plan_type || 'free',
            };
        },
        enabled: !!user,
    });

    const role: UserRole = userRoleData?.role || 'user';
    const isBanned = userRoleData?.is_banned || false;
    const planType = userRoleData?.plan_type || 'free';

    const isStaff = roleOrder[role] >= roleOrder['monitor'];

    const permissions: UserPermissions = {
        role,
        isMaster: role === 'master',
        isAdmin: role === 'admin' || role === 'master',
        isGerente: roleOrder[role] >= roleOrder['gerente'],
        isInstrutor: role === 'instrutor' || roleOrder[role] >= roleOrder['instrutor'],
        isMonitor: role === 'monitor' || roleOrder[role] >= roleOrder['monitor'],
        isModerator: roleOrder[role] >= roleOrder['moderator'],
        isUser: role === 'user',
        canModerate: roleOrder[role] >= roleOrder['moderator'] && !isBanned,
        canBanUsers: (role === 'master' || role === 'admin' || role === 'gerente') && !isBanned,
        canDeleteAnyContent: roleOrder[role] >= roleOrder['moderator'] && !isBanned,
        canPinPosts: roleOrder[role] >= roleOrder['moderator'] && !isBanned,
        canManageCourses: (role === 'master' || role === 'admin' || role === 'instrutor') && !isBanned,
        canManageLives: (role === 'master' || role === 'admin' || role === 'instrutor') && !isBanned,
        canViewReports: (role === 'master' || role === 'admin' || role === 'gerente') && !isBanned,
        canManageUsers: (role === 'master' || role === 'admin') && !isBanned,
        canManageRoles: (role === 'master' || role === 'admin') && !isBanned,
    };

    return {
        role,
        permissions,
        isBanned,
        isLoading,
        planType,
        isStaff,
    };
}

// Helper function to check if user can delete a specific post
export function canDeletePost(postUserId: string, currentUserId: string, permissions: UserPermissions): boolean {
    return postUserId === currentUserId || permissions.canDeleteAnyContent;
}

// Helper function to check if user can delete a specific comment
export function canDeleteComment(
    commentUserId: string,
    postUserId: string,
    currentUserId: string,
    permissions: UserPermissions
): boolean {
    return (
        commentUserId === currentUserId || // Own comment
        postUserId === currentUserId || // Post owner
        permissions.canDeleteAnyContent // Moderator+
    );
}
